/**
 * Client Calculations Engine
 * 
 * Provides reusable functions for client financial analysis:
 * - Revenue calculation
 * - Revenue dependency percentage
 * - Project counts
 * - Payment delay averages
 * 
 * These calculations are performed dynamically (in-memory),
 * not stored in the database.
 */

import type { Tables } from '@/integrations/supabase/types';

type Payment = Tables<'payments'>;
type Project = Tables<'projects'>;

// ============================================================================
// Types
// ============================================================================

export interface ClientRevenueSummary {
  totalRevenue: number; // Total paid amount
  totalPending: number;
  totalOverdue: number;
  totalExpected: number; // paid + pending + overdue
}

export interface ClientProjectSummary {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
}

export interface ClientPaymentBehavior {
  averageDelayDays: number; // Negative = pays early, Positive = pays late
  onTimePayments: number;
  latePayments: number;
  totalPayments: number;
  paymentReliabilityScore: number; // 0-100 scale
}

export interface ClientDependencyMetrics {
  revenuePercentage: number; // % of total user revenue this client represents
  isHighDependency: boolean; // > 30% = high dependency
  riskLevel: 'low' | 'medium' | 'high';
}

export interface ClientAnalytics {
  revenue: ClientRevenueSummary;
  projects: ClientProjectSummary;
  paymentBehavior: ClientPaymentBehavior;
  dependency: ClientDependencyMetrics;
}

// ============================================================================
// Revenue Calculations
// ============================================================================

/**
 * Calculate total revenue for a client based on payments
 */
export function calculateClientRevenue(
  payments: Payment[]
): ClientRevenueSummary {
  let totalRevenue = 0;
  let totalPending = 0;
  let totalOverdue = 0;

  payments.forEach((payment) => {
    const amount = Number(payment.amount) || 0;

    switch (payment.status) {
      case 'paid':
        totalRevenue += amount;
        break;
      case 'pending':
        totalPending += amount;
        break;
      case 'overdue':
        totalOverdue += amount;
        break;
    }
  });

  return {
    totalRevenue,
    totalPending,
    totalOverdue,
    totalExpected: totalRevenue + totalPending + totalOverdue,
  };
}

/**
 * Calculate client's revenue percentage compared to total user revenue
 */
export function calculateRevenuePercentage(
  clientRevenue: number,
  totalUserRevenue: number
): number {
  if (totalUserRevenue <= 0) return 0;
  return (clientRevenue / totalUserRevenue) * 100;
}

/**
 * Calculate dependency metrics for a client
 */
export function calculateClientDependency(
  clientRevenue: number,
  totalUserRevenue: number
): ClientDependencyMetrics {
  const revenuePercentage = calculateRevenuePercentage(clientRevenue, totalUserRevenue);
  const isHighDependency = revenuePercentage > 30;

  let riskLevel: 'low' | 'medium' | 'high' = 'low';
  if (revenuePercentage > 50) {
    riskLevel = 'high';
  } else if (revenuePercentage > 30) {
    riskLevel = 'medium';
  }

  return {
    revenuePercentage,
    isHighDependency,
    riskLevel,
  };
}

// ============================================================================
// Project Calculations
// ============================================================================

/**
 * Calculate project summary for a client
 */
export function calculateClientProjects(
  projects: Project[]
): ClientProjectSummary {
  const totalProjects = projects.length;
  const completedProjects = projects.filter((p) => p.status === 'done').length;
  const activeProjects = totalProjects - completedProjects;

  return {
    totalProjects,
    activeProjects,
    completedProjects,
  };
}

// ============================================================================
// Payment Behavior Analysis
// ============================================================================

/**
 * Calculate average payment delay for a client
 * Negative value = pays early on average
 * Positive value = pays late on average
 */
export function calculateAveragePaymentDelay(payments: Payment[]): number {
  const paidPaymentsWithDates = payments.filter(
    (p) => p.status === 'paid' && p.due_date && p.paid_date
  );

  if (paidPaymentsWithDates.length === 0) return 0;

  let totalDelayDays = 0;

  paidPaymentsWithDates.forEach((payment) => {
    const dueDate = new Date(payment.due_date!);
    const paidDate = new Date(payment.paid_date!);
    const delayMs = paidDate.getTime() - dueDate.getTime();
    const delayDays = Math.floor(delayMs / (1000 * 60 * 60 * 24));
    totalDelayDays += delayDays;
  });

  return Math.round(totalDelayDays / paidPaymentsWithDates.length);
}

/**
 * Calculate comprehensive payment behavior metrics
 */
export function calculatePaymentBehavior(
  payments: Payment[]
): ClientPaymentBehavior {
  const paidPaymentsWithDates = payments.filter(
    (p) => p.status === 'paid' && p.due_date && p.paid_date
  );

  const totalPayments = paidPaymentsWithDates.length;
  let onTimePayments = 0;
  let latePayments = 0;
  let totalDelayDays = 0;

  paidPaymentsWithDates.forEach((payment) => {
    const dueDate = new Date(payment.due_date!);
    const paidDate = new Date(payment.paid_date!);
    const delayMs = paidDate.getTime() - dueDate.getTime();
    const delayDays = Math.floor(delayMs / (1000 * 60 * 60 * 24));

    totalDelayDays += delayDays;

    // Consider "on time" if paid within 3 days of due date
    if (delayDays <= 3) {
      onTimePayments++;
    } else {
      latePayments++;
    }
  });

  const averageDelayDays =
    totalPayments > 0 ? Math.round(totalDelayDays / totalPayments) : 0;

  // Calculate reliability score (0-100)
  // 100 = all payments on time, decreases with late payments
  const paymentReliabilityScore =
    totalPayments > 0
      ? Math.round((onTimePayments / totalPayments) * 100)
      : 100; // No payments = assume reliable

  return {
    averageDelayDays,
    onTimePayments,
    latePayments,
    totalPayments,
    paymentReliabilityScore,
  };
}

// ============================================================================
// Combined Analytics
// ============================================================================

/**
 * Calculate all client analytics in one call
 */
export function calculateClientAnalytics(
  payments: Payment[],
  projects: Project[],
  totalUserRevenue: number
): ClientAnalytics {
  const revenue = calculateClientRevenue(payments);
  const projectSummary = calculateClientProjects(projects);
  const paymentBehavior = calculatePaymentBehavior(payments);
  const dependency = calculateClientDependency(revenue.totalRevenue, totalUserRevenue);

  return {
    revenue,
    projects: projectSummary,
    paymentBehavior,
    dependency,
  };
}

// ============================================================================
// Plan Access Check (Structural Preparation)
// ============================================================================

interface PlanStatus {
  plan_status?: string | null;
  trial_expired?: boolean | null;
}

/**
 * Check if user has access to client management features
 * Returns true if user can create/edit clients
 */
export function checkClientPlanAccess(profile: PlanStatus | null): boolean {
  if (!profile) return false;

  // Pro users always have access
  if (profile.plan_status === 'pro') return true;

  // Trial users have access unless expired
  if (profile.trial_expired === true) return false;

  return true;
}

/**
 * Get relationship status label in Portuguese
 */
export function getRelationshipStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    ativo: 'Ativo',
    inativo: 'Inativo',
    prospect: 'Prospect',
    perdido: 'Perdido',
  };
  return labels[status] || status;
}

/**
 * Get relationship status color class
 */
export function getRelationshipStatusColor(status: string): string {
  const colors: Record<string, string> = {
    ativo: 'bg-success/10 text-success',
    inativo: 'bg-muted text-muted-foreground',
    prospect: 'bg-primary/10 text-primary',
    perdido: 'bg-destructive/10 text-destructive',
  };
  return colors[status] || 'bg-muted text-muted-foreground';
}
